import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { create_lease_reminder } from "../../../../utils/urls";

const CreateReminder = ({ isDialog = false, onSuccess }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [reminderData, setReminderData] = useState({
    name: "",
    type: "standard",
    module: "lease",
    remindOn: {
      invoiceDate: true,
      dueDate: false,
      afterOverdue: {
        enabled: false,
        daily: false,
        days: []
      }
    },
    time: "09:00",
    notificationTypes: [],
    message: "",
    isActive: true
  });


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReminderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationTypeChange = (type) => {
    setReminderData(prev => {
      const currentTypes = [...prev.notificationTypes];
      const index = currentTypes.indexOf(type);

      if (index === -1) {
        currentTypes.push(type);
      } else {
        currentTypes.splice(index, 1);
      }

      return {
        ...prev,
        notificationTypes: currentTypes
      };
    });
  };

  const handleRemindOnChange = (field, value) => {
    setReminderData(prev => ({
      ...prev,
      remindOn: {
        ...prev.remindOn,
        [field]: typeof value === 'object' ? value : !!value
      }
    }));
  };

  const handleDailyOverdueChange = (value) => {
    setReminderData(prev => ({
      ...prev,
      remindOn: {
        ...prev.remindOn,
        afterOverdue: {
          ...prev.remindOn.afterOverdue,
          daily: value
        }
      }
    }));
  };

  const handleOverdueDaysChange = (day) => {
    setReminderData(prev => {
      const currentDays = [...prev.remindOn.afterOverdue.days];
      const index = currentDays.indexOf(day);

      if (index === -1) {
        currentDays.push(day);
      } else {
        currentDays.splice(index, 1);
      }

      return {
        ...prev,
        remindOn: {
          ...prev.remindOn,
          afterOverdue: {
            ...prev.remindOn.afterOverdue,
            days: currentDays.sort((a, b) => a - b)
          }
        }
      };
    });
  };

  const validateForm = () => {
    try {
      if (!reminderData.name?.trim()) {
        toastify("Reminder name is required", "error");
        return false;
      }

      if (!reminderData.notificationTypes.length) {
        toastify("At least one notification type is required", "error");
        return false;
      }

      const validTypes = reminderData.notificationTypes.every(type =>
        ['SMS', 'EMAIL'].includes(type.toUpperCase())
      );
      if (!validTypes) {
        toastify("Invalid notification types", "error");
        return false;
      }

      if (!reminderData.time.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
        toastify("Invalid time format", "error");
        return false;
      }

      if (reminderData.remindOn.afterOverdue.enabled &&
        !reminderData.remindOn.afterOverdue.daily &&
        !reminderData.remindOn.afterOverdue.days.length) {
        toastify("Either enable daily reminders or select at least one overdue reminder day", "error");
        return false;
      }

      // Check if at least one remind on option is selected
      if (!reminderData.remindOn.invoiceDate &&
        !reminderData.remindOn.dueDate &&
        !reminderData.remindOn.afterOverdue.enabled) {
        toastify("Select at least one reminder option", "error");
        return false;
      }

      if (reminderData.message && reminderData.message.length > 500) {
        toastify("Message cannot exceed 500 characters", "error");
        return false;
      }

      return true;
    } catch (error) {
      toastify("Form validation error", "error");
      return false;
    }
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm() || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found", "error");
        return;
      }

      const formData = {
        ...reminderData,
        moduleId: facilityId,
        remindOn: {
          ...reminderData.remindOn,
          afterOverdue: {
            ...reminderData.remindOn.afterOverdue,
            days: reminderData.remindOn.afterOverdue.days.map(Number)
          }
        },
        notificationTypes: reminderData.notificationTypes.map(type => type.toUpperCase()),
        message: reminderData.message.trim(),
        facilityId
      };

      const response = await makeRequest2(
        `${create_lease_reminder}/${facilityId}`,
        "POST",
        formData
      );

      if (response.success) {
        toastify("Reminder created successfully", "success");
        if (isDialog && onSuccess) {
          onSuccess();
        } else {
          navigate("/facility/lease_management/lease_penalties", {
            state: { activeTab: 'reminders' }
          });
        }
      } else {
        toastify(response.error || "Failed to create reminder", "error");
      }
    } catch (error) {
      toastify(
        error.response?.data?.error ||
        error.message ||
        "An unexpected error occurred",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setReminderData({
      name: "",
      type: "standard",
      remindOn: {
        invoiceDate: true,
        dueDate: false,
        afterOverdue: {
          enabled: false,
          daily: false,
          days: []
        }
      },
      time: "09:00",
      notificationTypes: [],
      message: ""
    });
  };

  const content = (
    <div className="card-body">
      <div className="row">
        <div className="col-sm-6">
          <label className="form-label">Reminder Name</label>
          <InputText
            className="form-control"
            name="name"
            value={reminderData.name}
            onChange={handleInputChange}
            placeholder="Enter reminder name"
          />
        </div>

        <div className="col-sm-6">
          <label className="form-label">Reminder Type</label>
          <select
            className="form-control"
            name="type"
            value={reminderData.type}
            onChange={handleInputChange}
          >
            <option value="standard">Standard</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="col-sm-6 mt-3">
          <label className="form-label">Notification Types</label>
          <div className="d-flex gap-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={reminderData.notificationTypes.includes('SMS')}
                onChange={() => handleNotificationTypeChange('SMS')}
                id="smsCheck"
              />
              <label className="form-check-label" htmlFor="smsCheck">
                SMS
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={reminderData.notificationTypes.includes('EMAIL')}
                onChange={() => handleNotificationTypeChange('EMAIL')}
                id="emailCheck"
              />
              <label className="form-check-label" htmlFor="emailCheck">
                Email
              </label>
            </div>
          </div>
        </div>

        <div className="col-sm-6 mt-3">
          <label className="form-label">Time</label>
          <InputText
            type="time"
            className="form-control"
            name="time"
            value={reminderData.time}
            onChange={handleInputChange}
          />
        </div>

        <div className="col-12 mt-4">
          <h6 className="mb-3">Remind On</h6>
          <div className="row">
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.invoiceDate}
                  onChange={(e) => handleRemindOnChange('invoiceDate', e.target.checked)}
                  id="invoiceDateCheck"
                />
                <label className="form-check-label" htmlFor="invoiceDateCheck">
                  Invoice Date
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.dueDate}
                  onChange={(e) => handleRemindOnChange('dueDate', e.target.checked)}
                  id="dueDateCheck"
                />
                <label className="form-check-label" htmlFor="dueDateCheck">
                  Due Date
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.afterOverdue.enabled}
                  onChange={(e) => handleRemindOnChange('afterOverdue', {
                    enabled: e.target.checked,
                    daily: reminderData.remindOn.afterOverdue.daily,
                    days: e.target.checked ? reminderData.remindOn.afterOverdue.days : []
                  })}
                  id="overdueCheck"
                />
                <label className="form-check-label" htmlFor="overdueCheck">
                  After Overdue
                </label>
              </div>
            </div>
          </div>
        </div>

        {reminderData.remindOn.afterOverdue.enabled && (
          <div className="col-12 mt-3">
            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={reminderData.remindOn.afterOverdue.daily}
                  onChange={(e) => handleDailyOverdueChange(e.target.checked)}
                  id="dailyOverdueCheck"
                />
                <label className="form-check-label" htmlFor="dailyOverdueCheck">
                  <strong>Send reminders every day after due date</strong>
                </label>
              </div>
            </div>

            {!reminderData.remindOn.afterOverdue.daily && (
              <div>
                <label className="form-label">Specific overdue reminder days:</label>
                <div className="d-flex gap-3">
                  {[1, 3, 7, 10].map(day => (
                    <div className="form-check" key={day}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={reminderData.remindOn.afterOverdue.days.includes(day)}
                        onChange={() => handleOverdueDaysChange(day)}
                        id={`day${day}Check`}
                      />
                      <label className="form-check-label" htmlFor={`day${day}Check`}>
                        {day} {day === 1 ? 'Day' : 'Days'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="col-12 mt-3">
          <label className="form-label">Custom Message (Optional)</label>
          <textarea
            className="form-control"
            name="message"
            value={reminderData.message}
            onChange={handleInputChange}
            rows="3"
            maxLength="500"
            placeholder="Enter custom reminder message"
          />
          <small className="text-muted">
            {reminderData.message.length}/500 characters
          </small>
        </div>
      </div>

      <div className="text-end mt-4">
        <Button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={handleClear}
        >
          Clear
        </Button>
        <Button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Reminder"}
        </Button>
      </div>
    </div>
  );

  if (isDialog) {
    return content;
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/lease_management/reminders">
                    Lease Reminders
                  </Link>
                </li>
                <li className="breadcrumb-item">Create Reminder</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h5>Create New Reminder</h5>
            </div>
            {content}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateReminder;